<script setup lang="ts">
import { ref, onMounted, inject, computed, watch, onUnmounted } from 'vue'
import type { Ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Switch } from '@/components/ui/switch'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { toast } from 'vue-sonner'
import {
	Folder,
	FolderOpen,
	Image,
	Users,
	Calendar,
	ArrowLeft,
	Save,
	Loader2,
	Brain,
	Database,
	BarChart3,
	TrendingUp,
	Play,
	Layers,
	RefreshCw,
} from 'lucide-vue-next'
import { useGroups } from '@/services/Groups'
import { useMedia } from '@/services/Media'
import { useMLTraining } from '@/services/MLTraining'
import type { Group } from '@/types/Groups'
import type { Member } from '@/types/Member'
import type { TrainingStats } from '@/services/MLTraining'
import MediaThumb from '@/components/MediaThumb.vue'

const route = useRoute()
const router = useRouter()
const vhostId = inject<Ref<string> | null>('vhostId', null)

const { getGroup, updateGroup, getUserGroups } = useGroups()
const { fetchMedia } = useMedia()
const { getTrainingStats, startTraining, checkTrainingStatus } = useMLTraining()

const group = ref<Group | null>(null)
const loading = ref(true)
const saving = ref(false)
const activeTab = ref('overview')

// Edit state
const editName = ref('')
const editDescription = ref('')
const enableCategoryTraining = ref(false)

// Media state
const mediaItems = ref<any[]>([])
const mediaLoading = ref(false)
const mediaTotalCount = ref(0)

// Members state
const members = ref<any[]>([])
const membersLoading = ref(false)

// Training state
const trainingStats = ref<TrainingStats>({
	totalSamples: 0,
	realCount: 0,
	fakeCount: 0,
	unexportedCount: 0,
	lastTrainingDate: null,
	lastAccuracy: null,
})
const trainingLoading = ref(false)
const trainingInProgress = ref(false)
const pollInterval = ref<ReturnType<typeof setInterval> | null>(null)

const groupId = computed(() => Number(route.params.id))
const vid = computed(() => vhostId?.value ? Number(vhostId.value) : 0)

const moderationLabel = computed(() => {
	if (!group.value) return 'Unknown'
	switch (group.value.moderationstatus) {
		case 1: return 'Approved'
		case 2: return 'Denied'
		case 8: return 'Deleted'
		default: return 'Unmoderated'
	}
})

const moderationVariant = computed(() => {
	if (!group.value) return 'outline' as const
	switch (group.value.moderationstatus) {
		case 1: return 'default' as const
		case 2: return 'destructive' as const
		default: return 'secondary' as const
	}
})

const canTrain = computed(() => {
	return trainingStats.value.unexportedCount >= 20 && !trainingInProgress.value
})

const accuracyPercent = computed(() => {
	if (trainingStats.value.lastAccuracy !== null) {
		return (trainingStats.value.lastAccuracy * 100).toFixed(1)
	}
	return null
})

const loadGroup = async () => {
	loading.value = true
	try {
		group.value = await getGroup(groupId.value)
		if (group.value) {
			editName.value = group.value.name || ''
			editDescription.value = group.value.description || ''
			// Parse category training setting from note field (JSON)
			try {
				const settings = group.value.note ? JSON.parse(group.value.note) : {}
				enableCategoryTraining.value = !!settings.enable_category_training
			} catch {
				enableCategoryTraining.value = false
			}
		}
	} catch (err) {
		console.error('Failed to load group:', err)
		toast('Error', { description: 'Failed to load group details.' })
	} finally {
		loading.value = false
	}
}

const loadMedia = async () => {
	if (!vid.value) return
	mediaLoading.value = true
	try {
		const result = await fetchMedia(
			vid.value,
			{ groupid: groupId.value },
			'upload DESC',
			0,
			24,
			['id', 'title', 'thumbUrl', 'filetype', 'status', 'upload', 'tags']
		)
		mediaItems.value = result.mediaData || []
		mediaTotalCount.value = result.totalCount || 0
	} catch (err) {
		console.error('Failed to load media:', err)
	} finally {
		mediaLoading.value = false
	}
}

const loadTrainingStats = async () => {
	if (!vid.value) return
	trainingLoading.value = true
	try {
		trainingStats.value = await getTrainingStats(vid.value, groupId.value)
	} catch (err) {
		console.error('Failed to load training stats:', err)
	} finally {
		trainingLoading.value = false
	}
}

const handleSave = async () => {
	if (!group.value) return
	saving.value = true
	try {
		// Build settings JSON for note field
		let existingSettings: Record<string, unknown> = {}
		try {
			existingSettings = group.value.note ? JSON.parse(group.value.note) : {}
		} catch {
			existingSettings = {}
		}
		existingSettings.enable_category_training = enableCategoryTraining.value

		const result = await updateGroup(groupId.value, {
			name: editName.value,
			description: editDescription.value,
			note: JSON.stringify(existingSettings),
		})
		if (result) {
			toast('Saved', { description: 'Group settings updated.' })
			await loadGroup()
		} else {
			toast('Error', { description: 'Failed to save group settings.' })
		}
	} catch (err) {
		console.error('Failed to save:', err)
		toast('Error', { description: 'Failed to save group settings.' })
	} finally {
		saving.value = false
	}
}

const handleStartTraining = async () => {
	if (!vid.value || trainingInProgress.value) return
	trainingInProgress.value = true

	const result = await startTraining(vid.value, groupId.value)
	if (result.success) {
		toast('Training Started', { description: result.message || 'Category model training initiated.' })
		startPolling()
	} else {
		toast('Training Failed', { description: result.message || 'Could not start training.' })
		trainingInProgress.value = false
	}
}

const startPolling = () => {
	if (pollInterval.value) return
	pollInterval.value = setInterval(async () => {
		if (!vid.value) return
		const status = await checkTrainingStatus(vid.value)
		if (status.status === 'completed' || status.status === 'failed') {
			trainingInProgress.value = false
			stopPolling()
			await loadTrainingStats()
			if (status.status === 'completed') {
				toast('Training Complete', { description: 'Category model has been updated.' })
			} else {
				toast('Training Failed', { description: status.error || 'Training encountered an error.' })
			}
		}
	}, 10000)
}

const stopPolling = () => {
	if (pollInterval.value) {
		clearInterval(pollInterval.value)
		pollInterval.value = null
	}
}

const formatDate = (dateStr: string | Date | null | undefined): string => {
	if (!dateStr) return '—'
	const d = new Date(dateStr)
	return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

// Load data when tab changes
watch(activeTab, (tab) => {
	if (tab === 'media' && mediaItems.value.length === 0) {
		loadMedia()
	}
	if (tab === 'settings') {
		loadTrainingStats()
	}
})

onMounted(() => {
	loadGroup()
})

onUnmounted(() => {
	stopPolling()
})
</script>

<template>
	<div class="p-4 space-y-4">
		<!-- Back button -->
		<div class="flex items-center gap-4">
			<Button variant="ghost" size="sm" @click="router.push('/groups')">
				<ArrowLeft class="h-4 w-4 mr-2" />
				Back to Groups
			</Button>
		</div>

		<!-- Loading -->
		<div v-if="loading" class="flex items-center justify-center py-20">
			<Loader2 class="h-8 w-8 animate-spin text-muted-foreground" />
		</div>

		<!-- Not found -->
		<div v-else-if="!group" class="text-center py-20 text-muted-foreground">
			<Folder class="h-12 w-12 mx-auto mb-4 opacity-40" />
			<p class="text-lg">Group not found</p>
		</div>

		<template v-else>
			<!-- Header -->
			<div class="flex items-start justify-between">
				<div class="flex items-center gap-3">
					<FolderOpen class="h-8 w-8 text-blue-500" />
					<div>
						<h1 class="text-2xl font-bold">{{ group.name }}</h1>
						<p v-if="group.description" class="text-muted-foreground text-sm mt-1" v-html="group.description" />
					</div>
				</div>
				<Badge :variant="moderationVariant">{{ moderationLabel }}</Badge>
			</div>

			<!-- Tabs -->
			<Tabs v-model="activeTab" class="w-full">
				<TabsList>
					<TabsTrigger value="overview">Overview</TabsTrigger>
					<TabsTrigger value="media">Media</TabsTrigger>
					<TabsTrigger value="settings">Settings</TabsTrigger>
				</TabsList>

				<!-- Overview Tab -->
				<TabsContent value="overview" class="space-y-4">
					<div class="grid gap-4 md:grid-cols-3">
						<Card>
							<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
								<CardTitle class="text-sm font-medium">Media</CardTitle>
								<Image class="h-5 w-5 text-blue-500" />
							</CardHeader>
							<CardContent>
								<div class="text-2xl font-bold">{{ (group.mediaCount || 0).toLocaleString() }}</div>
								<p class="text-xs text-muted-foreground">Files in this group</p>
							</CardContent>
						</Card>

						<Card>
							<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
								<CardTitle class="text-sm font-medium">Members</CardTitle>
								<Users class="h-5 w-5 text-green-500" />
							</CardHeader>
							<CardContent>
								<div class="text-2xl font-bold">{{ (group.memberCount || 0).toLocaleString() }}</div>
								<p class="text-xs text-muted-foreground">Users in this group</p>
							</CardContent>
						</Card>

						<Card>
							<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
								<CardTitle class="text-sm font-medium">Created</CardTitle>
								<Calendar class="h-5 w-5 text-purple-500" />
							</CardHeader>
							<CardContent>
								<div class="text-lg font-bold">{{ formatDate(group.created) }}</div>
								<p class="text-xs text-muted-foreground">Group creation date</p>
							</CardContent>
						</Card>
					</div>

					<!-- Parent group link -->
					<Card v-if="group.parentGroup && group.parentGroup > 0">
						<CardContent class="pt-4">
							<p class="text-sm text-muted-foreground">
								Parent Group:
								<span class="text-blue-600 cursor-pointer hover:underline" @click="router.push(`/groups/${group.parentGroup}`)">
									Group #{{ group.parentGroup }}
								</span>
							</p>
						</CardContent>
					</Card>

					<!-- Location info -->
					<Card v-if="group.city || group.country || group.geo_latitude">
						<CardHeader>
							<CardTitle class="text-sm font-medium">Location</CardTitle>
						</CardHeader>
						<CardContent>
							<p v-if="group.address" class="text-sm">{{ group.address }}</p>
							<p v-if="group.city || group.state || group.country" class="text-sm text-muted-foreground">
								{{ [group.city, group.state, group.country].filter(Boolean).join(', ') }}
							</p>
							<p v-if="group.geo_latitude && group.geo_longitude" class="text-xs text-muted-foreground mt-1">
								{{ group.geo_latitude }}, {{ group.geo_longitude }}
							</p>
						</CardContent>
					</Card>
				</TabsContent>

				<!-- Media Tab -->
				<TabsContent value="media" class="space-y-4">
					<div v-if="mediaLoading" class="flex items-center justify-center py-10">
						<Loader2 class="h-6 w-6 animate-spin text-muted-foreground" />
					</div>

					<div v-else-if="mediaItems.length === 0" class="text-center py-10 text-muted-foreground">
						<Image class="h-10 w-10 mx-auto mb-3 opacity-40" />
						<p>No media in this group</p>
					</div>

					<div v-else>
						<p class="text-sm text-muted-foreground mb-4">{{ mediaTotalCount.toLocaleString() }} items in this group</p>
						<div class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
							<div
								v-for="item in mediaItems"
								:key="item.id"
								class="cursor-pointer rounded-lg overflow-hidden border hover:border-blue-400 transition-colors"
								@click="router.push(`/media/${item.id}`)"
							>
								<div class="aspect-square bg-muted">
									<MediaThumb v-if="item.thumbUrl" :url="item.thumbUrl" :filetype="item.filetype" class="w-full h-full object-cover" />
								</div>
								<div class="p-2">
									<p class="text-xs font-medium truncate">{{ item.title || `#${item.id}` }}</p>
								</div>
							</div>
						</div>
					</div>
				</TabsContent>

				<!-- Settings Tab -->
				<TabsContent value="settings" class="space-y-6">
					<!-- Group Info Edit -->
					<Card>
						<CardHeader>
							<CardTitle class="text-lg">Group Information</CardTitle>
							<CardDescription>Edit the group name and description.</CardDescription>
						</CardHeader>
						<CardContent class="space-y-4">
							<div>
								<Label>Name</Label>
								<Input v-model="editName" class="mt-1" />
							</div>
							<div>
								<Label>Description</Label>
								<Input v-model="editDescription" class="mt-1" />
							</div>
						</CardContent>
					</Card>

					<!-- Category Training -->
					<Card>
						<CardHeader>
							<div class="flex items-center gap-2">
								<Brain class="h-5 w-5 text-indigo-500" />
								<CardTitle class="text-lg">Category Training</CardTitle>
							</div>
							<CardDescription>
								Train an ML model to validate whether content belongs in this group. When enabled, approving media confirms it fits this category; denying means it doesn't belong.
							</CardDescription>
						</CardHeader>
						<CardContent class="space-y-4">
							<div class="flex items-center justify-between">
								<div>
									<p class="text-sm font-medium">Enable Category Training</p>
									<p class="text-xs text-muted-foreground">Moderation decisions will train a category-specific model</p>
								</div>
								<Switch
									:checked="enableCategoryTraining"
									@update:checked="enableCategoryTraining = $event"
								/>
							</div>

							<!-- Training Stats (shown when enabled) -->
							<template v-if="enableCategoryTraining">
								<div class="border-t pt-4">
									<div class="flex items-center justify-between mb-4">
										<h3 class="text-sm font-medium">Training Data for this Group</h3>
										<Button variant="outline" size="sm" @click="loadTrainingStats" :disabled="trainingLoading">
											<RefreshCw class="h-3 w-3 mr-1" :class="{ 'animate-spin': trainingLoading }" />
											Refresh
										</Button>
									</div>

									<div v-if="trainingLoading" class="flex items-center justify-center py-6">
										<Loader2 class="h-5 w-5 animate-spin text-muted-foreground" />
									</div>

									<div v-else class="grid gap-3 md:grid-cols-4">
										<div class="p-3 bg-muted/50 rounded-lg">
											<div class="flex items-center gap-2 mb-1">
												<Database class="h-4 w-4 text-blue-500" />
												<span class="text-xs font-medium">Total</span>
											</div>
											<p class="text-lg font-bold">{{ trainingStats.totalSamples }}</p>
										</div>
										<div class="p-3 bg-muted/50 rounded-lg">
											<div class="flex items-center gap-2 mb-1">
												<BarChart3 class="h-4 w-4 text-purple-500" />
												<span class="text-xs font-medium">Belongs / Doesn't</span>
											</div>
											<p class="text-lg font-bold">
												<span class="text-green-600">{{ trainingStats.realCount }}</span>
												<span class="text-muted-foreground mx-1">/</span>
												<span class="text-red-600">{{ trainingStats.fakeCount }}</span>
											</p>
										</div>
										<div class="p-3 bg-muted/50 rounded-lg">
											<div class="flex items-center gap-2 mb-1">
												<Layers class="h-4 w-4 text-orange-500" />
												<span class="text-xs font-medium">Ready to Train</span>
											</div>
											<p class="text-lg font-bold">{{ trainingStats.unexportedCount }}</p>
										</div>
										<div class="p-3 bg-muted/50 rounded-lg">
											<div class="flex items-center gap-2 mb-1">
												<TrendingUp class="h-4 w-4 text-green-500" />
												<span class="text-xs font-medium">Accuracy</span>
											</div>
											<p class="text-lg font-bold">
												<template v-if="accuracyPercent !== null">{{ accuracyPercent }}%</template>
												<template v-else>—</template>
											</p>
										</div>
									</div>

									<!-- Start Training Button -->
									<div class="mt-4">
										<TooltipProvider>
											<Tooltip>
												<TooltipTrigger as-child>
													<div>
														<Button
															@click="handleStartTraining"
															:disabled="!canTrain"
															class="gap-2"
														>
															<template v-if="trainingInProgress">
																<Loader2 class="h-4 w-4 animate-spin" />
																Training in Progress...
															</template>
															<template v-else>
																<Play class="h-4 w-4" />
																Start Category Training
															</template>
														</Button>
													</div>
												</TooltipTrigger>
												<TooltipContent v-if="!canTrain && !trainingInProgress">
													<p v-if="trainingStats.unexportedCount < 20">Need at least 20 new samples (currently {{ trainingStats.unexportedCount }})</p>
												</TooltipContent>
											</Tooltip>
										</TooltipProvider>
									</div>

									<div v-if="trainingInProgress" class="mt-3 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg flex items-center gap-3">
										<Loader2 class="h-5 w-5 animate-spin text-blue-500" />
										<div>
											<p class="text-sm font-medium text-blue-700 dark:text-blue-300">Training in progress</p>
											<p class="text-xs text-blue-600 dark:text-blue-400">This may take several minutes.</p>
										</div>
									</div>
								</div>
							</template>
						</CardContent>
					</Card>

					<!-- Save Button -->
					<div class="flex justify-end">
						<Button @click="handleSave" :disabled="saving" class="gap-2">
							<Loader2 v-if="saving" class="h-4 w-4 animate-spin" />
							<Save v-else class="h-4 w-4" />
							{{ saving ? 'Saving...' : 'Save Settings' }}
						</Button>
					</div>
				</TabsContent>
			</Tabs>
		</template>
	</div>
</template>
